import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Users, MagnifyingGlass, CaretRight, Star, Crown, Buildings, PencilSimple } from "@phosphor-icons/react";
import DeleteWorkflowButton from "@/components/DeleteWorkflowButton";
import EditUserModal from "@/components/EditUserModal";
import { useAuth } from "@/contexts/AuthContext";

// Department colour mapping — mirrors backend DEPT_COLORS
const DEPT_BAND = {
  Executive:      { bar: "bg-blue-500",    chip: "bg-blue-500/10 text-blue-300 border-blue-500/30" },
  Consulting:     { bar: "bg-teal-500",    chip: "bg-teal-500/10 text-teal-300 border-teal-500/30" },
  Investigations: { bar: "bg-amber-500",   chip: "bg-amber-500/10 text-amber-300 border-amber-500/30" },
  Legal:          { bar: "bg-purple-500",  chip: "bg-purple-500/10 text-purple-300 border-purple-500/30" },
  Finance:        { bar: "bg-slate-500",   chip: "bg-slate-500/10 text-[var(--text-faint)] border-slate-500/30" },
  HR:             { bar: "bg-slate-500",   chip: "bg-slate-500/10 text-[var(--text-faint)] border-slate-500/30" },
  Quality:        { bar: "bg-teal-500",    chip: "bg-teal-500/10 text-teal-300 border-teal-500/30" },
  Operations:     { bar: "bg-blue-500",    chip: "bg-blue-500/10 text-blue-300 border-blue-500/30" },
};

const DEPT_TABS = ["All", "CEO's Office", "Consulting", "Investigations", "Legal", "Finance & HR"];

function deptBucket(dept) {
  if (dept === "Executive") return "CEO's Office";
  if (dept === "Consulting" || dept === "Quality") return "Consulting";
  if (dept === "Investigations" || dept === "Operations") return "Investigations";
  if (dept === "Legal") return "Legal";
  if (dept === "Finance" || dept === "HR") return "Finance & HR";
  return "All";
}

export default function TeamDirectory() {
  const { user: me } = useAuth();
  const canEdit = (me?.ux_tier || 0) >= 4;
  const [team, setTeam] = useState([]);
  const [tab, setTab] = useState("All");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/team").then(r => setTeam(r.data || [])).catch(() => setTeam([]));
  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const departments = new Set(team.map(u => u.department)).size;
    return {
      total: team.length,
      rms: team.filter(u => u.rm_md === "RM").length,
      mds: team.filter(u => u.rm_md === "MD").length,
      depts: departments,
    };
  }, [team]);

  const filtered = useMemo(() => {
    let list = team;
    if (tab !== "All") list = list.filter(u => deptBucket(u.department) === tab);
    if (q.trim()) {
      const ql = q.toLowerCase();
      list = list.filter(u =>
        (u.name || "").toLowerCase().includes(ql) ||
        (u.initials || "").toLowerCase().includes(ql) ||
        (u.role || "").toLowerCase().includes(ql) ||
        (u.department || "").toLowerCase().includes(ql),
      );
    }
    return list;
  }, [team, tab, q]);

  return (
    <div className="space-y-6" data-testid="team-directory">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Users size={22} weight="duotone" />
          <h1 className="font-display text-3xl font-bold">Team Directory</h1>
        </div>
        <p className="text-[13px] text-[var(--span-text-2)]">SPAN India staff with reporting hierarchy, RM/MD designations and responsibilities.</p>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Tile label="Total team" value={stats.total} testid="tile-total" />
        <Tile label="Relationship Managers" value={stats.rms} chipClass="text-sky-300" testid="tile-rms" />
        <Tile label="Mentor Directors" value={stats.mds} chipClass="text-amber-300" testid="tile-mds" />
        <Tile label="Departments" value={stats.depts} testid="tile-depts" />
      </div>

      {/* Filter tabs + search */}
      <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
        <div className="flex flex-wrap gap-1.5">
          {DEPT_TABS.map(t => (
            <button
              key={t}
              data-testid={`tab-${t.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
              onClick={() => setTab(t)}
              className={`px-3 py-1.5 text-[11px] font-mono tracking-wider uppercase border ${tab === t ? "bg-white text-black border-white" : "border-[var(--span-border)] text-[var(--span-text-2)] hover:text-white"}`}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="relative flex-1">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--span-text-2)]" />
          <input
            data-testid="team-search"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search by name, initials, role…"
            className="span-input pl-9 text-[13px]"
          />
        </div>
      </div>

      {/* People cards grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="team-cards">
        {filtered.map(u => (
          <PersonCard key={u.user_id} u={u} onClick={() => setSelected(u)} />
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center text-[13px] text-[var(--span-text-2)] py-12">
            No teammates match this filter.
          </div>
        )}
      </div>

      {selected && <DetailDrawer person={selected} onClose={() => setSelected(null)} canEdit={canEdit} onEdit={() => setEditing(selected)} />}
      {editing && (
        <EditUserModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); setSelected(null); load(); }}
        />
      )}
    </div>
  );
}

function Tile({ label, value, chipClass = "", testid }) {
  return (
    <div className="border border-[var(--span-border)] p-4" data-testid={testid}>
      <div className={`text-3xl font-bold font-mono ${chipClass}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-[var(--span-text-2)] mt-1">{label}</div>
    </div>
  );
}

function PersonCard({ u, onClick }) {
  const band = DEPT_BAND[u.department] || DEPT_BAND.Operations;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`person-card-${u.initials}`}
      className="text-left border border-[var(--span-border)] hover:border-white/30 transition-colors group"
    >
      <div className={`h-1 ${band.bar}`} />
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className={`w-12 h-12 flex items-center justify-center font-mono text-[14px] font-bold border ${band.chip}`}>
            {u.initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <div className="font-medium text-[14px] truncate">{u.name}</div>
              {u.rm_md === "MD" && <span className="font-mono text-[8px] tracking-[0.18em] px-1.5 py-px bg-amber-500/15 text-amber-300 border border-amber-500/30">MD</span>}
              {u.rm_md === "RM" && <span className="font-mono text-[8px] tracking-[0.18em] px-1.5 py-px bg-sky-500/15 text-sky-300 border border-sky-500/30">RM</span>}
              {u.dual_role && <span className="font-mono text-[8px] tracking-[0.18em] px-1.5 py-px bg-purple-500/15 text-purple-300 border border-purple-500/30">DUAL</span>}
            </div>
            <div className="text-[11px] text-[var(--span-text-2)] truncate">{u.role}</div>
            <div className="text-[10px] text-[var(--span-text-2)]/70 truncate mt-0.5 font-mono">{u.email}</div>
          </div>
          <CaretRight size={14} className="text-[var(--span-text-2)]/60 mt-2 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
        </div>
      </div>
    </button>
  );
}

function DetailDrawer({ person, onClose, canEdit, onEdit }) {
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    api.get(`/users/${person.user_id}/profile`).then(r => setProfile(r.data)).catch(() => setProfile(person));
  }, [person]);
  const p = profile || person;
  const band = DEPT_BAND[p.department] || DEPT_BAND.Operations;

  return (
    <div className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm flex items-stretch justify-end" data-testid="person-drawer">
      <div className="bg-[var(--span-bg-2)] border-l border-[var(--span-border)] w-full max-w-md overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className={`h-1.5 ${band.bar}`} />
        <div className="p-6 border-b border-[var(--span-border)]">
          <div className="flex items-start gap-4">
            <div className={`w-16 h-16 flex items-center justify-center font-mono text-[18px] font-bold border ${band.chip}`}>
              {p.initials}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-display text-xl font-bold">{p.name}</h2>
                {p.rm_md === "MD" && <span className="font-mono text-[9px] tracking-[0.18em] px-2 py-0.5 bg-amber-500/15 text-amber-300 border border-amber-500/30">MENTOR DIRECTOR</span>}
                {p.rm_md === "RM" && <span className="font-mono text-[9px] tracking-[0.18em] px-2 py-0.5 bg-sky-500/15 text-sky-300 border border-sky-500/30">RELATIONSHIP MANAGER</span>}
                {p.dual_role && <span className="font-mono text-[9px] tracking-[0.18em] px-2 py-0.5 bg-purple-500/15 text-purple-300 border border-purple-500/30">DUAL ROLE</span>}
              </div>
              <div className="text-[12px] text-[var(--span-text-2)] mt-1">{p.role}</div>
              <div className="text-[11px] text-[var(--span-text-2)]/70 font-mono mt-1">{p.email}</div>
              <div className="text-[10px] text-[var(--span-text-2)]/70 mt-1">{p.department}</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="drawer-close" className="absolute top-4 right-4 text-[var(--span-text-2)] hover:text-white">✕</button>
        </div>

        <Section title="Lifecycle">
          {canEdit && (
            <button
              onClick={onEdit}
              data-testid="open-edit-modal"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 mb-3 bg-[var(--brand-primary,#263776)] text-white text-[11px] font-semibold rounded hover:opacity-90">
              <PencilSimple size={12} /> Edit details
            </button>
          )}
          {p.deleted_at ? (
            <div className="text-[12px]"><span className="badge badge-danger">deleted</span> on {String(p.deleted_at).slice(0, 10)} · {p.deletion_reason}</div>
          ) : (
            <DeleteWorkflowButton entityType="user" entityId={p.user_id} entityLabel={`${p.name} (${p.email})`} variant="link" />
          )}
        </Section>

        {profile?.reporting_line?.length > 1 && (
          <Section title="Reporting line">
            <div className="space-y-2">
              {(profile.reporting_line || []).map((u, i) => (
                <div key={u.user_id} className="flex items-center gap-2 text-[12px]">
                  <span className="font-mono text-[10px] text-[var(--span-text-2)]/60 w-6">L{profile.reporting_line.length - 1 - i}</span>
                  <span className="font-mono text-[11px] px-1.5 py-px border border-[var(--span-border)]">{u.initials}</span>
                  <span>{u.name}</span>
                  <span className="text-[10px] text-[var(--span-text-2)]/70">· {u.role}</span>
                </div>
              ))}
            </div>
          </Section>
        )}

        {profile?.direct_reports?.length > 0 && (
          <Section title={`Direct reports (${profile.direct_reports.length})`}>
            <div className="grid grid-cols-2 gap-2">
              {(profile.direct_reports || []).map(r => (
                <div key={r.user_id} className="flex items-center gap-2 text-[12px] border border-[var(--span-border)] p-2">
                  <span className="font-mono text-[10px] px-1.5 py-px border border-[var(--span-border)]">{r.initials}</span>
                  <div className="min-w-0">
                    <div className="truncate">{r.name}</div>
                    <div className="text-[10px] text-[var(--span-text-2)]/70 truncate">{r.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {profile?.responsibilities?.length > 0 && (
          <Section title="Roles & responsibilities">
            <ul className="space-y-2 text-[12px] leading-relaxed">
              {(profile.responsibilities || []).map((r, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-[var(--span-text-2)]/50 mt-1.5 shrink-0">·</span>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {(profile?.rm || profile?.md) && (
          <Section title="Auto-derived for cases">
            {profile?.rm && (
              <div className="flex items-center gap-2 text-[12px] mb-2">
                <span className="font-mono text-[8px] tracking-[0.18em] px-1.5 py-px bg-sky-500/15 text-sky-300 border border-sky-500/30">RM</span>
                <span>{profile.rm.name} ({profile.rm.initials})</span>
              </div>
            )}
            {profile?.md && (
              <div className="flex items-center gap-2 text-[12px]">
                <span className="font-mono text-[8px] tracking-[0.18em] px-1.5 py-px bg-amber-500/15 text-amber-300 border border-amber-500/30">MD</span>
                <span>{profile.md.name} ({profile.md.initials})</span>
              </div>
            )}
          </Section>
        )}
      </div>
      <div className="flex-1 lg:max-w-none" onClick={onClose} />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="p-6 border-b border-[var(--span-border)]">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--span-text-2)] mb-3">{title}</div>
      {children}
    </div>
  );
}
